import {
  ComponentRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTheme } from '@material-ui/core';
import Button from 'components/button';
import NumberPad from 'components/number-pad';
import { useModal } from 'context/ModalContext';
import { ModalTypes } from 'enums/ModalTypes';
import { handleError } from 'helpers/handleError';
import {
  useCheckDataConfirmation,
  useBiometryFailure,
  useCheckSunExposureQuestion,
  useCheckForIncidences,
  useTermsConfirmation,
} from 'pages/Checkin/hooks';
import { FiCalendar, FiClock, FiAlertCircle } from 'react-icons/fi';

import LisaChatBubblePy from 'assets/images/lisa-balao-py.png';
import LisaCheckin from 'assets/images/lisa-convida.png';
import LisaChatBubble from 'assets/images/tela-totem-digital-baloon-text.png';
import DigitalIcon from 'assets/images/tela-totem-digital-icon.png';
import Receipt from 'assets/svgs/receipt.svg';
import { ReactComponent as TouchIconGreen } from 'assets/svgs/touch-icon-green.svg';
import { makeCheckinPubSub } from 'shared/infra/pubsubs';
import {
  useLocale,
  useUnit,
  useTotemInfo,
  useNavigation,
  TRoutePathsWithParams,
} from 'shared/presentation/contexts';
import { usePerformCheckin } from 'shared/presentation/hooks';

import {
  CheckSunExposureModal,
  ConfirmDataModal,
  IncidencesModal,
  NoBiometryAlert,
  TermsConfirmationModal,
  UpdateDataModal,
} from './components';
import * as S from './styles';

const Checkin: React.FC = () => {
  const theme = useTheme();
  const navigation = useNavigation();
  const { showModal, handleClose } = useModal();
  const { version } = useTotemInfo();
  const { unit } = useUnit();
  const { t, language } = useLocale('translations');

  const confirmDataRef = useRef<ComponentRef<typeof ConfirmDataModal>>(null);
  const updateDataRef = useRef<ComponentRef<typeof UpdateDataModal>>(null);
  const biometryAlertRef = useRef<ComponentRef<typeof NoBiometryAlert>>(null);
  const sunExposureModalRef =
    useRef<ComponentRef<typeof CheckSunExposureModal>>(null);
  const incidencesModalRef = useRef<ComponentRef<typeof IncidencesModal>>(null);
  const termsModalRef =
    useRef<ComponentRef<typeof TermsConfirmationModal>>(null);

  const [doingCpfCheckin, setDoingCpfCheckin] = useState(false);

  const [checkInError, setCheckInError] = useState('');
  const [checkAttempts, setCheckAttempts] = useState(0);

  const [loginAction, setLoginAction] = useState<TRoutePathsWithParams>('/');

  const increaseCheckinAttempt = useCallback(
    (biometry = false) => {
      const nextAttempt = checkAttempts + 1;

      setCheckAttempts(nextAttempt);

      if (nextAttempt === 1 && biometry) {
        showModal({ type: 'BIOMETRIA' });
        return;
      }
      if (nextAttempt > 1) {
        cpfValidationControl();
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkAttempts, showModal],
  );

  const handleTreatableError = useCallback(
    (error: unknown) => {
      setTimeout(() => handleClose(), 10000);
      const { message, type, response } = handleError(error);

      increaseCheckinAttempt();

      if (type && Object.values(ModalTypes).includes(type as ModalTypes)) {
        showModal({ message, type, response });
      } else {
        showModal({
          message,
          type: undefined,
          response,
        });
      }
    },
    [handleClose, increaseCheckinAttempt, showModal],
  );

  const confirmDataController = useCheckDataConfirmation({
    onError: handleTreatableError,
  });
  const checkSunExposureController = useCheckSunExposureQuestion({
    onError: handleTreatableError,
  });
  const checkIncidencesController = useCheckForIncidences({
    onError: handleTreatableError,
  });
  const checkTermsController = useTermsConfirmation({
    onError: handleTreatableError,
  });
  const checkinController = usePerformCheckin({
    onSuccess: ({ customerName }) => {
      handleClose();
      setDoingCpfCheckin(false);

      navigation.push(
        loginAction !== '/' ? loginAction : `/thank/${customerName}`,
      );
    },
    onError: error => handleTreatableError(error),
  });

  const isDataConfirmationOnCheckinAvailable = useMemo(
    () => !!version && version !== 'legacy',
    [version],
  );

  useBiometryFailure({
    onFailedTwice: () => {
      setDoingCpfCheckin(true);
    },
  });

  const handleCheckinWithId = useCallback(
    async (id: number) => {
      if (!unit) return;

      return checkinController.checkin({ customerId: id });
    },
    [unit, checkinController],
  );

  const handleCheckIfHasIncidencesById = useCallback(
    async (customerId: number) => {
      const shouldShowIncidences =
        await checkIncidencesController.shouldQuestionAboutIncidences({
          id: customerId,
        });

      if (!shouldShowIncidences) {
        return handleCheckinWithId(customerId);
      }

      incidencesModalRef.current?.open();
    },
    [handleCheckinWithId, checkIncidencesController],
  );

  const handleCheckIfHasSunExposureById = useCallback(
    async (customerId: number) => {
      const shouldQuestionAboutSunExposure =
        await checkSunExposureController.shouldQuestionAboutSunExposure({
          id: customerId,
        });

      if (!shouldQuestionAboutSunExposure) {
        return handleCheckIfHasIncidencesById(customerId);
      }

      sunExposureModalRef.current?.open();
    },
    [handleCheckIfHasIncidencesById, checkSunExposureController],
  );

  const handleCheckIfNeedsToConsentToTermsById = useCallback(
    async (customerId: number) => {
      const shouldConsentToTerms =
        await checkTermsController.shouldConsentToTerms({
          id: customerId,
        });

      if (!shouldConsentToTerms) {
        return handleCheckIfHasSunExposureById(customerId);
      }

      termsModalRef.current?.open();
    },
    [handleCheckIfHasSunExposureById, checkTermsController],
  );

  const handleCheckinWithCpf = useCallback(
    async (value: string) => {
      if (!unit) return;

      return checkinController.checkin({
        customerDocument: value,
      });
    },
    [checkinController, unit],
  );

  const handleCheckIfHasIncidencesByDocument = useCallback(
    async (document: string) => {
      const shouldShowIncidences =
        await checkIncidencesController.shouldQuestionAboutIncidences({
          document,
        });

      if (!shouldShowIncidences) {
        return handleCheckinWithCpf(document);
      }

      incidencesModalRef.current?.open();
    },
    [handleCheckinWithCpf, checkIncidencesController],
  );

  const handleCheckIfHasSunExposureByDocument = useCallback(
    async (document: string) => {
      const shouldQuestionAboutSunExposure =
        await checkSunExposureController.shouldQuestionAboutSunExposure({
          document,
        });

      if (!shouldQuestionAboutSunExposure) {
        return handleCheckIfHasIncidencesByDocument(document);
      }

      sunExposureModalRef.current?.open();
    },
    [handleCheckIfHasIncidencesByDocument, checkSunExposureController],
  );

  const handleCheckIfNeedsToConsentToTermsByDocument = useCallback(
    async (document: string) => {
      const shouldConsentToTerms =
        await checkTermsController.shouldConsentToTerms({
          document,
        });

      if (!shouldConsentToTerms) {
        return handleCheckIfHasSunExposureByDocument(document);
      }

      termsModalRef.current?.open();
    },
    [handleCheckIfHasSunExposureByDocument, checkTermsController],
  );

  const handleCheckConfirmationWithId = useCallback(
    async (customerId: number) => {
      if (!isDataConfirmationOnCheckinAvailable) {
        return handleCheckIfNeedsToConsentToTermsById(customerId);
      }

      const { isConfirmed, hasBiometry } =
        await confirmDataController.isDataConfirmed({
          id: customerId,
        });

      if (isConfirmed) {
        return handleCheckIfNeedsToConsentToTermsById(customerId);
      }

      if (!hasBiometry) return biometryAlertRef.current?.open();

      confirmDataRef.current?.open();
    },
    [
      confirmDataController,
      handleCheckIfNeedsToConsentToTermsById,
      isDataConfirmationOnCheckinAvailable,
    ],
  );

  useEffect(() => {
    const checkinPubSub = makeCheckinPubSub({
      type: version,
      unitId: unit?.id,
    });

    const unsubscribe = checkinPubSub.subscribeForIdentification(
      async ({ customerId }) => {
        setLoginAction('/');

        return handleCheckConfirmationWithId(customerId);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [version, handleCheckConfirmationWithId, unit?.id]);

  const cpfValidationControl = () => {
    setCheckAttempts(0);
    // setCheckInError('');
    setDoingCpfCheckin(true);
  };

  const handleConfirmDataFromDocument = async (document: string) => {
    if (!isDataConfirmationOnCheckinAvailable) {
      return handleCheckIfNeedsToConsentToTermsByDocument(document);
    }

    const { isConfirmed, hasBiometry } =
      await confirmDataController.isDataConfirmed({
        document,
      });

    if (isConfirmed) {
      return handleCheckIfNeedsToConsentToTermsByDocument(document);
    }

    setDoingCpfCheckin(false);

    if (!hasBiometry) return biometryAlertRef.current?.open();

    confirmDataRef.current?.open();
  };

  const isSpanishLanguage =
    language === 'es' || language === 'es-CR' || language === 'es-UY';

  return (
    <S.Checkin id="checkin-container">
      <S.Main>
        <img id="lisa" src={LisaCheckin} alt="lisa convida" />
        <S.Fingerprint src={DigitalIcon} alt="digitalIcon" />

        <S.LisaPopup>
          <img
            src={isSpanishLanguage ? LisaChatBubblePy : LisaChatBubble}
            alt="chatBubble"
          />
        </S.LisaPopup>

        <S.Actions isSpanishLanguage={isSpanishLanguage}>
          {/*
            QR Code removido temporariamente conforme decisão técnica (ED-6644).
            Problemas identificados na geração/consumo do hash da URL (unit/tenant/version)
            e inconsistência no padrão de roteamento (?hash vs /:hash).

            Decisão: ocultar o QR Code no Totem.
          */}
          {/* {!!unit && (
            <QRCode
              logoImage={Icon}
              qrStyle="dots"
              value={getUrlQrCode}
              bgColor={theme.colors.background}
              fgColor={theme.colors.primary}
              size={200}
            />
          )} */}

          <S.ButtonTouch>
            <Button
              color={theme.colors.white}
              background={theme.colors.primary}
              onClick={() => {
                setCheckInError('');
                setDoingCpfCheckin(true);
                setLoginAction('/');
              }}
            >
              {t('pages.home.actions.cpf_checkin')}{' '}
              {language === 'es-CR' && ' / DIMEX'}
            </Button>

            <TouchIconGreen />
          </S.ButtonTouch>
        </S.Actions>

        {doingCpfCheckin && (
          <NumberPad
            open={doingCpfCheckin}
            error={checkInError}
            onSubmit={document => {
              return handleConfirmDataFromDocument(document);
            }}
            handleClose={() => {
              handleClose();
              setLoginAction('/');
              setDoingCpfCheckin(false);
            }}
          />
        )}
      </S.Main>

      <S.Menu>
        <Button
          bordercolor={theme.colors.primary}
          icon={<FiCalendar size={16} color={theme.palette.text.hint} />}
          onClick={() => navigation.push('/new-schedule')}
          dataCy="newScheduleButton"
        >
          {t('pages.home.actions.new_schedule')}
        </Button>

        <Button
          bordercolor={theme.colors.primary}
          icon={<FiClock size={16} color={theme.palette.text.hint} />}
          onClick={() => navigation.push('/my-schedules')}
        >
          {t('pages.home.actions.my_schedules')}
        </Button>

        <Button
          icon={<img src={Receipt} alt="icon" />}
          bordercolor={theme.colors.primary}
          onClick={() => navigation.push('/sign-contracts')}
        >
          {t('pages.home.actions.sign_contract')}
        </Button>

        {!unit && (
          <S.ErrorAlert>
            <FiAlertCircle />

            <span>{t('pages.checkin.error_message')}</span>
          </S.ErrorAlert>
        )}
      </S.Menu>

      {!!confirmDataController.customerData && (
        <>
          <ConfirmDataModal
            ref={confirmDataRef}
            customer={confirmDataController.customerData}
            onUpdate={() => {
              updateDataRef.current?.open();
            }}
            onConfirm={customer =>
              handleCheckIfNeedsToConsentToTermsById(customer.id)
            }
          />

          <UpdateDataModal
            ref={updateDataRef}
            customer={confirmDataController.customerData}
            onSuccess={async customer =>
              handleCheckIfNeedsToConsentToTermsById(customer.id)
            }
          />
        </>
      )}

      {!!checkTermsController.termsData && (
        <TermsConfirmationModal
          ref={termsModalRef}
          {...checkTermsController.termsData}
          onReset={checkTermsController.resetData}
          onContinue={id => {
            handleCheckIfHasSunExposureById(id);
          }}
        />
      )}

      {!!checkSunExposureController.questionData && (
        <CheckSunExposureModal
          ref={sunExposureModalRef}
          {...checkSunExposureController.questionData}
          onDoCheckin={({ customerId, to }) => {
            if (to) setLoginAction(to);
            handleCheckIfHasIncidencesById(customerId);
          }}
        />
      )}

      {!!checkIncidencesController.incidenceData && (
        <IncidencesModal
          ref={incidencesModalRef}
          {...checkIncidencesController.incidenceData}
          onSuccess={customer => handleCheckinWithId(customer.id)}
        />
      )}

      <NoBiometryAlert ref={biometryAlertRef} />
    </S.Checkin>
  );
};

export default Checkin;
